import { createContext, useContext, useCallback, useEffect, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FRAMEWORK_ROUTES, type SessionDto } from '@framework/core';
import { clearCapabilities } from '../data/capability-store';

export type Session = SessionDto;

interface AuthValue {
  session: Session | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthValue>({
  session: null, loading: true,
  login: async () => 'not ready', logout: async () => {},
});

/** Identity the persisted section cache was written under. */
const IDENTITY_KEY = 'acfi.identity';

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  // Anonymous is a valid state, not an error â€” the site renders for everyone.
  const { data, isLoading } = useQuery<Session | null>({
    queryKey: ['session'],
    queryFn: async () => (await fetch(FRAMEWORK_ROUTES.me, { credentials: 'include' })).json(),
    staleTime: Infinity,
  });

  /**
   * `canWrite` is derived from the session, but it rides along in the persisted
   * section cache. If the cache was written under a different identity — signed in
   * last visit, cookie has since expired — the restored entries would show pencils
   * to someone who can't use them.
   *
   * So: once the session resolves, compare it to who the cache belongs to and
   * revalidate if it changed. That costs one batched request which returns CURRENT
   * with no payloads, because the versions still match.
   */
  useEffect(() => {
    if (isLoading) return;

    // Signed in but with no roles granted yet: nothing is editable, so drop every
    // capability outright rather than waiting for a response to correct them.
    if (data && data.roles.length === 0) clearCapabilities();

    const current = data?.userId ?? 'anon';
    if (localStorage.getItem(IDENTITY_KEY) !== current) {
      localStorage.setItem(IDENTITY_KEY, current);
      clearCapabilities();
      void qc.invalidateQueries({ queryKey: ['section'] });
    }
  }, [isLoading, data, qc]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await fetch(FRAMEWORK_ROUTES.login, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      // Surfaces PENDING_APPROVAL / REJECTED, not just bad credentials.
      const body = await res.json().catch(() => ({}));
      return body.message ?? 'Invalid email or password.';
    }
    qc.setQueryData(['session'], await res.json());
    // Roles changed â‡’ canWrite changed â‡’ every section must be re-evaluated.
    await qc.invalidateQueries({ queryKey: ['section'] });
    return null;
  }, [qc]);

  const logout = useCallback(async () => {
    await fetch(FRAMEWORK_ROUTES.logout, { method: 'POST', credentials: 'include' });
    qc.setQueryData(['session'], null);
    clearCapabilities();
    await qc.invalidateQueries({ queryKey: ['section'] });
  }, [qc]);

  return (
    <Ctx.Provider value={{ session: data ?? null, loading: isLoading, login, logout }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
